import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, CreditCard, ExternalLink, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import CreditsCard from '../CreditsCard';
import AutoTopUpCard from './AutoTopUpCard';
import { api, CREDITS_CHANGED_EVENT } from '../../context/AuthContext';

const PLAN_ORDER = ['base', 'standard', 'advanced'];
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
}) : '');
const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};
const creditsChanged = () => window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT));

/** Settings → Billing: plan, credits, packs, plan picker (monthly/annual) and auto-top-up. */
const BillingTab = () => {
  const [subscription, setSubscription] = useState(null);
  const [catalog, setCatalog] = useState({ plans: {}, credit_packs: {} });
  const [config, setConfig] = useState({});
  const [interval, setBillingInterval] = useState('monthly');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [subRes, plansRes, configRes] = await Promise.all([
      api.get('/billing/subscription'), api.get('/billing/plans'), api.get('/billing/config'),
    ]);
    setSubscription(subRes.data);
    setCatalog(plansRes.data);
    setConfig(configRes.data);
    if (subRes.data?.interval === 'annual') setBillingInterval('annual');
  }, []);

  useEffect(() => { load().catch(() => toast.error('Could not load billing')); }, [load]);

  // Back from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('cancelled')) toast.info('Checkout cancelled. Nothing was charged.');
    if (sessionId) {
      api.get(`/billing/checkout/status/${sessionId}`).then((res) => {
        if (res.data.status === 'complete') {
          toast.success(res.data.kind === 'pack' ? 'Credits added to your account' : 'Your subscription is active');
          load();
          creditsChanged();
        } else if (res.data.status === 'expired') {
          toast.error('That checkout session expired');
        }
      }).catch(() => {});
    }
    if (sessionId || params.get('cancelled')) {
      window.history.replaceState({}, '', `${window.location.pathname}?tab=billing`);
    }
  }, [load]);

  // "Buy credits" from the out-of-credits dialog lands on the packs
  useEffect(() => {
    if (subscription && window.location.hash === '#credit-packs') {
      document.getElementById('credit-packs')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [subscription]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const goTo = (url) => { window.location.href = url; };

  const choosePlan = (planId) => run(`plan-${planId}`, async () => {
    try {
      if (subscription?.has_stripe_subscription) {
        const res = await api.post('/billing/change-plan', { plan_id: planId, interval });
        if (res.data.action === 'portal') { goTo(res.data.url); return; }
        toast.success('Plan upgraded. Your extra credits are ready.');
      } else {
        const res = await api.post('/billing/checkout', { plan_id: planId, interval });
        if (res.data.url) { goTo(res.data.url); return; }
        toast.success(res.data.message || 'Subscription updated');
      }
      await load();
      creditsChanged();
    } catch (error) {
      toast.error(errorText(error, 'Could not change your plan'));
    }
  });

  const buyPack = (packId) => run(`pack-${packId}`, async () => {
    try {
      const res = await api.post('/billing/checkout-pack', { pack_id: packId });
      if (res.data.url) { goTo(res.data.url); return; }
      toast.success(res.data.message || 'Credits added');
      creditsChanged();
    } catch (error) {
      toast.error(errorText(error, 'Could not start checkout'));
    }
  });

  const openPortal = () => run('portal', async () => {
    try {
      goTo((await api.post('/billing/portal')).data.url);
    } catch (error) {
      toast.error(errorText(error, 'Could not open billing management'));
    }
  });

  const setCancel = (cancel) => run('cancel', async () => {
    if (cancel && !window.confirm('Cancel your subscription? You keep your plan and credits until the end of '
      + 'the current period.')) return;
    try {
      const res = await api.post(cancel ? '/billing/cancel' : '/billing/resume');
      toast.success(res.data.message);
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Could not update your subscription'));
    }
  });

  if (!subscription) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const plans = catalog.plans || {};
  const packs = catalog.credit_packs || {};
  const plan = subscription.plan || {};
  const isPaid = subscription.plan_id !== 'free_trial';
  const annual = subscription.interval === 'annual';
  const status = subscription.cancel_at_period_end
    ? { label: `Cancels ${formatDate(subscription.current_period_end)}`, className: 'bg-amber-100 text-amber-700' }
    : subscription.status === 'past_due'
      ? { label: 'Payment failed', className: 'bg-red-100 text-red-700' }
      : isPaid ? { label: 'Active', className: 'bg-emerald-100 text-emerald-700' }
        : { label: 'Free', className: 'bg-slate-100 text-slate-700' };
  const currentIndex = PLAN_ORDER.indexOf(subscription.plan_id);

  return (
    <div className="grid gap-6">
      {subscription.status === 'past_due' && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="flex-1">
            We couldn&apos;t charge your card. Update your payment method within 7 days to keep your plan.
          </div>
          <Button size="sm" variant="outline" onClick={openPortal} disabled={busy === 'portal'}>Update card</Button>
        </div>
      )}
      {subscription.lapsed_plan_id && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          Your {plans[subscription.lapsed_plan_id]?.name || ''} plan has ended. Choose a plan below to continue.
          Your purchased credits are still available.
        </div>
      )}

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Manage your subscription</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{plan.name || 'Free Trial'}</h3>
                <Badge className={status.className}>{status.label}</Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                {plan.calendars} calendars &bull; {plan.users} users &bull; {plan.social_accounts} social accounts
                &bull; {(plan.monthly_credits || 0).toLocaleString()} AI credits/month
              </p>
              {isPaid && subscription.current_period_end && !subscription.cancel_at_period_end && (
                <p className="text-xs text-slate-500 mt-1">Renews {formatDate(subscription.current_period_end)}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">
                ${annual ? plans[subscription.plan_id]?.annual_price : plan.price || 0}
                <span className="text-sm font-normal text-slate-500">/{annual ? 'year' : 'month'}</span>
              </p>
            </div>
          </div>
          {subscription.has_stripe_subscription && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={openPortal} disabled={busy === 'portal'}
                data-testid="manage-billing-btn">
                <ExternalLink className="h-4 w-4 mr-2" />Manage billing &amp; invoices
              </Button>
              {subscription.cancel_at_period_end ? (
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => setCancel(false)} disabled={busy === 'cancel'}>Resume subscription</Button>
              ) : (
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setCancel(true)}
                  disabled={busy === 'cancel'}>Cancel subscription</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <CreditsCard />

      <Card className="border-slate-100" id="credit-packs">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-emerald-500" />Buy credits</CardTitle>
          <CardDescription>One-time packs on top of your plan. Pack credits never expire.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(packs).map(([packId, pack]) => (
              <div key={packId} className="rounded-lg border-2 border-slate-200 p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{pack.credits.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mb-3">credits · ${pack.price}</p>
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => buyPack(packId)}
                  disabled={busy === `pack-${packId}`} data-testid={`buy-${packId}-btn`}>
                  {busy === `pack-${packId}` && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Buy
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Plans</CardTitle>
            <CardDescription>Every plan includes all AI tools; plans differ in monthly credits and team size.</CardDescription>
          </div>
          <div className="flex rounded-lg bg-slate-100 p-1 text-sm" role="group" aria-label="Billing interval">
            {['monthly', 'annual'].map((value) => (
              <button key={value} type="button" onClick={() => setBillingInterval(value)} data-testid={`interval-${value}`}
                className={`rounded-md px-3 py-1 ${interval === value ? 'bg-white shadow-sm font-medium' : 'text-slate-600'}`}>
                {value === 'monthly' ? 'Monthly' : 'Annual · 2 months free'}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLAN_ORDER.filter((id) => plans[id]).map((planId, index) => {
              const p = plans[planId];
              const isCurrent = subscription.plan_id === planId && (subscription.interval || 'monthly') === interval;
              const label = isCurrent ? 'Current plan'
                : !subscription.has_stripe_subscription ? 'Subscribe'
                  : index > currentIndex || (index === currentIndex && interval === 'annual') ? 'Upgrade' : 'Switch';
              return (
                <div key={planId} className={`p-4 rounded-lg border-2 transition-colors ${
                  isCurrent ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-900">{p.name}</h4>
                    {isCurrent && <Badge className="bg-emerald-500 text-white">Current</Badge>}
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    ${interval === 'annual' ? Math.round(p.annual_monthly_equivalent) : p.price}
                    <span className="text-sm font-normal text-slate-500">/mo</span>
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    {interval === 'annual' ? `$${p.annual_price.toLocaleString()} billed yearly` : 'billed monthly'}
                  </p>
                  <ul className="space-y-2 mb-4 text-sm">
                    {p.features?.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500 shrink-0" />{feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isCurrent ? 'outline' : 'default'}
                    className={`w-full ${!isCurrent ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : ''}`}
                    disabled={isCurrent || busy === `plan-${planId}`}
                    onClick={() => choosePlan(planId)}
                    data-testid={`subscribe-${planId}-btn`}
                  >
                    {busy === `plan-${planId}` && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{label}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 text-center mt-4 flex items-center justify-center gap-1">
            <CreditCard className="h-3.5 w-3.5" />
            {!config.payments_enabled
              ? 'Demo mode: plan changes apply instantly and nothing is charged.'
              : config.mode === 'test'
                ? 'Test mode: use card 4242 4242 4242 4242 with any future date and CVC.'
                : 'Payments are processed securely by Stripe. Upgrades are prorated; downgrades apply at renewal.'}
          </p>
        </CardContent>
      </Card>

      <AutoTopUpCard packs={packs} />
    </div>
  );
};

export default BillingTab;
